import React, { useMemo } from 'react'
import styles from './ProgressBar.module.scss'
// Убедитесь, что пути к типам и константам корректны
import AccountForm from '../../types/account-form.types'
import { Lang } from '../../types/common-types'
import {
  profileType_Physical,
  profileType_Fiscal,
} from '../../state-actions-reducers'
import { INSTANCE } from '../../server-parameters'
import cn from 'classnames'

interface Props {
  step: number // Текущий активный шаг (1-индексированный)
  totalsteps: number // Общее количество шагов
  stepsText: string // Описательный текст для всего прогресс-бара (если нужен)
  stepsAriaLabel: string // ARIA-метка для всего прогресс-бара
  className?: string
  accountForm: AccountForm // Объект с текстами для заголовков шагов
  lang: Lang // Текущий язык
  profileType: any // Тип профиля (лучше заменить на более конкретный enum/union type)
}

// Цвета и переменные для стилизации
const activeColor = '#222222'        // Цвет активного текста заголовка
const defaultColor = '#B3B3B3'       // Цвет неактивного текста заголовка
const activeIndicator = 'var(--accent-color)' // Цвет активного индикатора (кружок, точка, линия)
const defaultIndicator = '#E4E5E7'   // Цвет неактивного индикатора (кружок, линия)

interface StepConfig {
  id: string
  titleKey: keyof AccountForm // Ключ для доступа к тексту заголовка в accountForm
}

const ProgressBar = ({
  step: currentStep,
  totalsteps,
  stepsText,
  stepsAriaLabel,
  className,
  accountForm,
  lang,
  profileType,
}: Props) => {
  // Мемоизированная конфигурация шагов, чтобы избежать пересчетов при каждом рендере
  const stepsConfiguration = useMemo((): StepConfig[] => {
    let baseSteps: StepConfig[] = []

    if (profileType === profileType_Fiscal) {
      baseSteps = [
        { id: 'fiscal1', titleKey: 'stepFiscalTitleOne' },
        { id: 'fiscal2', titleKey: 'stepFiscalTitleTwo' },
        { id: 'fiscal3', titleKey: 'stepFiscalTitleThree' },
        { id: 'fiscal4', titleKey: 'stepFiscalTitleFour' }
      ]
    } else { // Предполагается profileType_Physical или другой тип по умолчанию
      baseSteps = [
        { id: 'physical1', titleKey: 'stepPassportTitle' },
        { id: 'physical2', titleKey: 'stepContactTitle' },
        { id: 'physical3', titleKey: 'stepRentTitle' }
      ]
    }
    return baseSteps
  }, [profileType, lang, accountForm, INSTANCE]) // Добавлен INSTANCE в зависимости useMemo

  // Если шагов нет, компонент не рендерится
  if (!stepsConfiguration || stepsConfiguration.length === 0) {
    return null
  }

  return (
    <div
      className={`${styles.progressBarBox} ${className || ''}`}
      role="progressbar"
      aria-valuenow={currentStep}
      aria-valuemin={1}
      aria-valuemax={stepsConfiguration.length || totalsteps} // Используем длину конфигурации или totalsteps
      aria-label={stepsAriaLabel || 'Прогресс выполнения'}
    >
      <div className={styles.progressBarRender}>
        {stepsConfiguration.map((stepConfig, index) => {
          const stepNumber = index + 1 // Номер шага (1, 2, 3...)
          const isStepActive = currentStep >= stepNumber // Активен ли текущий кружок/заголовок
          // Линия ПОСЛЕ текущего кружка активна, если следующий шаг уже достигнут или пройден
          const isLineAfterThisStepActive = currentStep > stepNumber

          // Получаем текст заголовка из accountForm
          // Добавлена проверка на существование titleKey в accountForm для большей надежности
          const titleText = accountForm[stepConfig.titleKey] && typeof accountForm[stepConfig.titleKey] === 'object'
            ? (accountForm[stepConfig.titleKey] as any)[lang]
            : `Шаг ${stepNumber}`;
          const title = titleText || `Шаг ${stepNumber}`;


          return (
            <React.Fragment key={stepConfig.id}>
              {/* Блок для заголовка и кружка */}
              <div className={cn(styles.progressStepItem, INSTANCE === 'second' ? styles.circle : styles.square)}>
                <h4
                  className={styles.stepTitle}
                  style={{ color: isStepActive ? activeColor : defaultColor }}
                  aria-current={currentStep === stepNumber ? "step" : undefined}
                >
                  {title}
                </h4>
                <span
                  className={INSTANCE === 'second' ? styles.indicatorCircle : styles.indicatorSquare}
                  style={{
                    borderColor: isStepActive ? activeIndicator : defaultIndicator,
                    backgroundColor: INSTANCE !== 'second' ? isStepActive ? activeIndicator : defaultIndicator : 'transparent'
                  }}
                >
                </span>
              </div>

              {/* Соединительная линия (рендерится если это не последний шаг) */}
              {index < stepsConfiguration.length - 1 && (
                <div
                  className={INSTANCE === 'second' ? styles.connectingLineCircle : styles.connectingLineSquare}
                  style={{
                    background: isLineAfterThisStepActive
                      ? activeIndicator
                      : defaultIndicator,
                  }}
                />
              )}
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}

export default ProgressBar